import omit from 'lodash/omit';
import { mockFunctionType } from 'utils/tsTestHelpers';
import { Button } from 'modules/graphql';
import { ReactNode } from 'react';
import { HelpTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { SelectiveTargetingOptionValues } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/DeliverySection/SelectiveTargeting/SelectiveTargeting.constants';
import { EmbeddedActionButton } from 'modules/templateEditor/EditEmbedded/constants';
import { EmbeddedDisplayUntil } from 'modules/domains/campaign';
import { DateFormat, TimeFormat, localizeDateTime } from 'utils/l10n';
import styled from 'styled-components';
import { LabelWithHelpIcon } from './components/PlacementsCreate/styles';
import {
  ActionButtonToggle,
  MediaLayout,
  Placement,
  EmbeddedDestination,
  PlacementCreateInput,
  PlacementType,
  PlacementsCreateFormState,
} from './types';
import {
  DEFAULT_DISPLAY_PRIORITY_CONTEXT_STATE,
  useDisplayPriorityContext,
} from './context/DisplayPriorityContext';

const StyledHelpTooltip = styled(HelpTooltip)`
  &:focus {
    &:before {
      border: 0;
    }
  }
`;

export const mockDisplayPriorityContext = (
  overrides?: Partial<ReturnType<typeof useDisplayPriorityContext>>,
) =>
  mockFunctionType(useDisplayPriorityContext).mockReturnValue({
    ...DEFAULT_DISPLAY_PRIORITY_CONTEXT_STATE,
    ...overrides,
  });

export const addHelpIconTooltip = (label: string, tooltipContent: string) => (
  <LabelWithHelpIcon>
    <span>{label}</span>
    <StyledHelpTooltip
      tooltipContent={tooltipContent}
      iconProps={{
        width: 16,
        height: 16,
        color: colors.iconPrimary,
      }}
    />
  </LabelWithHelpIcon>
);

export const reformatLabelForId = (label: ReactNode | string) =>
  label?.toString().toLowerCase().split(' ').join('-');

export const toggleToActionButton = (actionButtonToggles: ActionButtonToggle[]): Button[] =>
  actionButtonToggles
    ?.filter((toggle) => toggle.isActive)
    ?.map((toggle) => ({
      title: toggle.title,
      id: toggle.title,
      action: {
        type: EmbeddedActionButton.URL,
        data: '',
      },
    })) ?? [];

export const getPlacementCreateDefaultValues = (): PlacementsCreateFormState => {
  return {
    placementName: '',
    description: '',
    mediaLayout: MediaLayout.TOP,
    placementType: PlacementType.Banner,
    campaignLimit: 1,
    hasTitle: true,
    hasBody: true,
    hasMedia: true,
    hasDataFeeds: true,
    hasOpenAction: true,
    actionButtons: [
      { title: 'Button 1', isActive: true },
      { title: 'Button 2', isActive: true },
    ],
    payload: '{}',
    destinations: SelectiveTargetingOptionValues.EVERYTHING,
    textFields: [],
  };
};

export const transformPlacementToFormValues = (placement: Placement): PlacementsCreateFormState => {
  return {
    placementName: `Copy of ${placement.placementName}`,
    description: placement.description || '',
    mediaLayout: placement.mediaLayout || MediaLayout.TOP,
    placementType: placement.campaignLimit > 1 ? PlacementType.Carousel : PlacementType.Banner,
    campaignLimit: placement.campaignLimit || 1,
    hasTitle: placement.hasTitle || true,
    hasBody: placement.hasBody || true,
    hasMedia: placement.hasMedia || true,
    hasDataFeeds: placement.hasDataFeeds || true,
    hasOpenAction: placement.hasOpenAction || true,
    actionButtons:
      placement.actionButtons?.map((button, index) => ({
        title: button.title || `Button ${index + 1}`,
        isActive: true,
      })) || [],
    payload: placement.payload ? JSON.stringify(placement.payload) : '{}',
    destinations: SelectiveTargetingOptionValues.ALL_MOBILE_APPS,
    textFields: placement.textFields || [],
  };
};

export const transformPlacementFormToInput = (
  form: PlacementsCreateFormState,
): PlacementCreateInput => {
  const getDestination = (destination: SelectiveTargetingOptionValues) => {
    if (destination === SelectiveTargetingOptionValues.EVERYTHING) {
      return [EmbeddedDestination.Browser, EmbeddedDestination.Mobile];
    } else if (destination === SelectiveTargetingOptionValues.ALL_MOBILE_APPS) {
      return [EmbeddedDestination.Mobile];
    } else {
      return [EmbeddedDestination.Browser];
    }
  };
  const input = {
    ...omit(form, ['placementType']),
    actionButtons: toggleToActionButton(form.actionButtons),
    destinations: getDestination(form.destinations),
  };
  return input;
};

export const getLabelForDisplayUntilForJourneyCampaign = (displayUntil?: EmbeddedDisplayUntil) => {
  const endDateFormatted =
    displayUntil?.endDate &&
    localizeDateTime(new Date(displayUntil.endDate), {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateStyle: DateFormat.Numeric,
      timeStyle: TimeFormat.Locale,
      showTime: true,
    });

  if (displayUntil?.searchQuery?.searchQueries?.[0]) {
    return `User field changes: ${displayUntil.searchQuery.searchQueries[0].field}`;
  } else if (endDateFormatted) {
    return `Date is ${endDateFormatted}`;
  }
  return 'Journey is off or archived';
};
